'use client';

import { useId, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import type { DrugInteraction, NoInteraction } from '../types';

const severityOrder = {
  critical: 0,
  important: 1,
  moderate: 2,
  minor: 3,
} as const;

type Translator = (key: string, options?: Record<string, unknown>) => string;

function joinWith(parts: string[], conjunction: string): string {
  if (parts.length <= 1) {
    return parts[0] ?? '';
  }

  if (parts.length === 2) {
    return `${parts[0]} ${conjunction} ${parts[1]}`;
  }

  return `${parts.slice(0, -1).join(', ')} ${conjunction} ${parts[parts.length - 1]}`;
}

function formatDrugLabel(drug: {
  productName: string;
  commonName: string;
}): string {
  return drug.commonName
    ? `${drug.productName} (${drug.commonName})`
    : drug.productName;
}

function getPairLabel(interaction: DrugInteraction): string {
  return `${formatDrugLabel(interaction.pair.drug1)} + ${formatDrugLabel(interaction.pair.drug2)}`;
}

function getSafePairLabel(pair: NoInteraction): string {
  return `${formatDrugLabel(pair.pair.drug1)} + ${formatDrugLabel(pair.pair.drug2)}`;
}

function formatGroupedPairNarrative(
  t: Translator,
  severity: DrugInteraction['severity'] | 'safe',
  pairLabels: string[],
  isFirstGroup: boolean,
): string {
  const pairs = joinWith(pairLabels, t('commentary.andAlt'));
  const count = pairLabels.length;

  const narrativeKey =
    severity === 'moderate' && !isFirstGroup
      ? 'commentary.narrative.moderateAlso'
      : `commentary.narrative.${severity}`;
  const narrative = t(narrativeKey, { count, pairs });

  const explanation =
    severity === 'safe' ? null : t(`commentary.explanation.${severity}`);

  return explanation ? `${narrative}. ${explanation}` : narrative;
}

interface InteractionPharmacologicalCommentaryProps {
  interactions: DrugInteraction[];
  safePairs: NoInteraction[];
}

export function InteractionPharmacologicalCommentary({
  interactions,
  safePairs,
}: InteractionPharmacologicalCommentaryProps) {
  const { t, i18n } = useTranslation('interactions');
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const language = i18n.language;

  const { topPairNarratives, severitySummary, hasMultipleSeverities } =
    useMemo(() => {
      const sortedInteractions = [...interactions].sort((a, b) => {
        const severityDiff =
          severityOrder[a.severity] - severityOrder[b.severity];

        if (severityDiff !== 0) {
          return severityDiff;
        }

        const pairA = `${a.pair.drug1.productName} ${a.pair.drug2.productName}`;
        const pairB = `${b.pair.drug1.productName} ${b.pair.drug2.productName}`;

        return pairA.localeCompare(pairB, language);
      });

      const counts = sortedInteractions.reduce(
        (summary, interaction) => {
          summary[interaction.severity] += 1;
          return summary;
        },
        {
          critical: 0,
          important: 0,
          moderate: 0,
          minor: 0,
        },
      );

      const severitySummaryParts = (
        ['critical', 'important', 'moderate', 'minor'] as const
      )
        .filter((severity) => counts[severity] > 0)
        .map((severity) =>
          t(`commentary.severityCount.${severity}`, {
            count: counts[severity],
          }),
        );

      const selectedInteractionPairs = sortedInteractions
        .slice(0, 3)
        .map((interaction) => ({
          severity: interaction.severity,
          label: getPairLabel(interaction),
        }));

      const remainingSlots = Math.max(0, 3 - selectedInteractionPairs.length);
      const selectedSafePairs = safePairs
        .slice(0, remainingSlots)
        .map((pair) => ({
          severity: 'safe' as const,
          label: getSafePairLabel(pair),
        }));

      const selectedPairs = [...selectedInteractionPairs, ...selectedSafePairs];
      const groupedPairs = selectedPairs.reduce<
        Array<{
          severity: DrugInteraction['severity'] | 'safe';
          labels: string[];
        }>
      >((groups, pair) => {
        const lastGroup = groups[groups.length - 1];

        if (lastGroup && lastGroup.severity === pair.severity) {
          lastGroup.labels.push(pair.label);
          return groups;
        }

        groups.push({
          severity: pair.severity,
          labels: [pair.label],
        });

        return groups;
      }, []);

      return {
        topPairNarratives: groupedPairs.map((group, index) =>
          formatGroupedPairNarrative(
            t,
            group.severity,
            group.labels,
            index === 0,
          ),
        ),
        severitySummary: joinWith(severitySummaryParts, t('commentary.and')),
        hasMultipleSeverities: severitySummaryParts.length > 1,
      };
    }, [interactions, safePairs, t, language]);

  if (interactions.length === 0 && safePairs.length === 0) {
    return null;
  }

  const safePairsSentence =
    safePairs.length > 0
      ? t('commentary.safePairsSentence', { count: safePairs.length })
      : '';
  const totalInteractionsSentence = t('commentary.totalInteractions', {
    count: interactions.length,
  });

  const found = hasMultipleSeverities
    ? t('commentary.foundTotal', {
        total: totalInteractionsSentence,
        summary: severitySummary,
      })
    : severitySummary;
  const foundParagraph =
    t('commentary.foundIntro', { found }) +
    (safePairs.length > 0
      ? `. ${t('commentary.additionally', { sentence: safePairsSentence.toLowerCase() })}`
      : '.');

  return (
    <section className="mt-1 pt-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={panelId}
        className="ml-auto flex w-fit items-baseline justify-end gap-1.5 py-0.5 text-right"
      >
        <span className="text-[10px] font-semibold tracking-[0.18em] text-zinc-400 uppercase dark:text-zinc-500">
          {t('commentary.title')}
        </span>
        <span className="text-xs text-zinc-500 dark:text-zinc-400">
          {t('commentary.interactionsCount', { count: interactions.length })}
        </span>
      </button>

      <div
        id={panelId}
        aria-hidden={!open}
        className={cn(
          'overflow-hidden transition-all duration-200',
          open ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <div className="mt-1 space-y-2 rounded-md bg-zinc-50/70 px-3 py-2.5 text-[13px] leading-6 text-zinc-600 dark:bg-zinc-900/45 dark:text-zinc-300">
          <p>
            {interactions.length > 0
              ? foundParagraph
              : t('commentary.noneFound')}
          </p>

          {topPairNarratives.map((narrative) => (
            <p key={narrative}>{narrative}.</p>
          ))}

          <p>{t('commentary.basedOn')}</p>

          <p>
            {hasMultipleSeverities && interactions.length + safePairs.length > 1
              ? t('commentary.perspectiveMulti')
              : t('commentary.perspectiveSingle')}
          </p>
        </div>
      </div>
    </section>
  );
}
